import { Dispatch, SetStateAction } from 'react';
import { useLocalStorageState } from '@msrceng/component';
import { chain, merge, reduce } from 'lodash';
import {
  IFilterCommonState,
  useCommonFilterState,
  useInteractionDelayEffect,
} from './util';
import { FilterData, makeDispatchNameFrom } from './filterContext';
import {
  IAsyncQueryController,
  Filter,
  GroupByOption,
  IOrderBy,
  IFilterAsAlways,
  IFilterBase,
} from '../../types';

export const useListFilterState = <TFilterTypeState,>({
  filterSettings,
  defaultOrderBy,
  defaultGroupName,
  tableName,
  groupByOptionList,
  resetQuery,
}: IUseListFilterProps<TFilterTypeState>): IUseListFilterState => {
  const commonFilterState = useCommonFilterState(
    tableName,
    defaultOrderBy,
    defaultGroupName,
    groupByOptionList
  );
  const [customFilterStateFromStorage, setFilterList, resetFilterList] =
    useLocalStorageState<FilterData>(`${tableName}-customFilterState`, {});
  const [isFiltered, setIsFiltered] = useLocalStorageState<boolean>(
    'isFiltered',
    false
  );
  // Re-query on filter changes
  const { onInteract, queryControl } = useInteractionDelayEffect(() => {}, [
    commonFilterState.startDate,
    commonFilterState.endDate,
    commonFilterState.orderBy,
    customFilterStateFromStorage,
  ]);

  /** This is a bunch of fake state dispatchers designed to
   * mimic the existing filter "setValue" Dispatch's
   * created by the useState hook.
   */
  const filterSetterList: {
    [key: string]: Dispatch<SetStateAction<any[]>>;
  } = reduce(
    filterSettings,
    (acc, { dataName, type }) => {
      if (type === 'always') {
        return acc;
      }
      //create a setter for this filter
      const setter = value => {
        if (!customFilterStateFromStorage) {
          return;
        }
        customFilterStateFromStorage[dataName] = value;
        const hasNonEmptyList = Object.values(
          customFilterStateFromStorage
        ).some(val => Array.isArray(val) && val.length > 0);

        setIsFiltered(hasNonEmptyList);
        return setFilterList(customFilterState => merge({}, customFilterState));
      };
      //add that setter to our setter bag
      acc[makeDispatchNameFrom(dataName)] = setter;
      //return updated bag
      return acc;
    },
    {} //empty accumulator to start off the reduce
  );

  const customFilterStateFromAlwaysFilters = chain(filterSettings)
    .filter<IFilterBase & IFilterAsAlways>(
      (filter): filter is IFilterAsAlways & IFilterBase =>
        filter.type === 'always'
    )
    .reduce(
      (acc, { dataName, value }) => ({
        ...acc,
        [dataName]: value,
      }),
      {}
    )
    .value();

  const clearFilter = () => {
    setIsFiltered(false);
    setFilterList({});
    commonFilterState.resetFilterText();
    commonFilterState.resetGroupBy();
    commonFilterState.resetOrderBy();
    resetFilterList();
    resetQuery && resetQuery();
  };
  const clearShared = () => {
    commonFilterState.resetStartDate();
    commonFilterState.resetEndDate();
    resetQuery && resetQuery();
  };
  return {
    isFiltered,
    commonFilterState,
    customFilterState: {
      ...filterSetterList,
      ...customFilterStateFromStorage,
      ...customFilterStateFromAlwaysFilters,
    },
    filterStateList: {
      ...commonFilterState,
      ...filterSetterList,
      ...customFilterStateFromStorage,
      ...customFilterStateFromAlwaysFilters,
    },
    queryControl,
    filterSettings,
    groupByOptionList,
    clearFilter,
    clearShared,
    onInteract,
    setIsFiltered,
  };
};

export interface IUseListFilterState {
  isFiltered: boolean;
  commonFilterState: IFilterCommonState;
  customFilterState: FilterData;
  filterStateList: IFilterCommonState & FilterData;
  queryControl: IAsyncQueryController;
  filterSettings?: Filter[];
  groupByOptionList?: GroupByOption[];
  clearFilter: () => void;
  clearShared: () => void;
  onInteract: () => void;
  setIsFiltered: (isFiltered: boolean) => void;
}

export interface IUseListFilterProps<TFilterTypeState> {
  setFilter?: (query: TFilterTypeState) => void;
  filterSettings?: Filter[];
  tableName: string;
  defaultOrderBy?: IOrderBy;
  defaultGroupName?: string;
  groupByOptionList?: GroupByOption[];
  resetQuery?: () => void;
}
